import { useCallback } from 'react';
import { logError } from 'utils/logger';
import { useLazyQuery, useMutation } from '@apollo/client';
import { MutationHookOptions } from '@apollo/client/react/types/types';

import { invoiceApi } from '../api';
import { collectInvoiceFromVO } from '../lib';
import { useNotifications } from '../../notification';
import { UpdateInvoiceResponse, UpdateInvoiceVariables } from '../api/types';

export function useInvoice() {
  const { showNotification } = useNotifications();

  const [fetchInvoice] = useLazyQuery(invoiceApi.getByID);

  const [updateInvoice, { loading: isInvoiceUpdating }] = useMutation<
    UpdateInvoiceResponse,
    Partial<UpdateInvoiceVariables>
  >(invoiceApi.update);

  const handleFetchInvoice = async (id: string) => {
    try {
      const { data } = await fetchInvoice({ variables: { id } });

      if (!data?.invoice) return null;

      return collectInvoiceFromVO(data.invoice)!;
    } catch (error) {
      logError(error);
      return null;
    }
  };

  const handleUpdateInvoice = useCallback(
    async (variables: Partial<UpdateInvoiceVariables>) => {
      try {
        await updateInvoice({ variables });
      } catch (error) {
        logError(error);
      }
    },
    [updateInvoice]
  );

  const [applyFeesAndTaxes, { loading: isApplyingFeesAndTaxes }] = useMutation(invoiceApi.applyCharges);

  const handleApplyFeesAndTaxes = useCallback(
    async (
      voId: string,
      quoteId: string,
      fetchOptions?: (api: typeof invoiceApi) => Omit<MutationHookOptions, 'variables'>
    ) => {
      try {
        const { data } = await applyFeesAndTaxes({
          variables: { input: { voId, quoteId } },
          ...(typeof fetchOptions === 'function' ? fetchOptions(invoiceApi) : {}),
        });

        if (data?.result?.error || !data?.result?.success) {
          showNotification(data?.result?.error ?? 'Something went wrong', { type: 'error' });
          return;
        }

        showNotification('Fees and Taxes applied successfully!');
      } catch (error) {
        showNotification('Something went wrong', { type: 'error' });
        logError(error);
      }
    },
    [applyFeesAndTaxes, showNotification]
  );

  const [onUpload, { loading: isUploading }] = useMutation(invoiceApi.uploadInvoiceDocument);

  const handleUploadInvoice = useCallback(
    async (
      file: File,
      dto: Record<string, any>,
      fetchOptions?: (api: typeof invoiceApi) => Omit<MutationHookOptions, 'variables'>
    ) => {
      try {
        const { data } = await onUpload({
          variables: {
            file,
            input: {
              taxAmount: 0,
              invoiceTotal: 0,
              source: 'portal',
              docName: 'will_be_changed_by_server',
              createdBy: new Date().toString(),
              ...dto,
            },
          },
          ...(typeof fetchOptions === 'function' ? fetchOptions(invoiceApi) : {}),
        });

        if (!data?.upsertDocument) {
          showNotification('Something went wrong', { type: 'error' });
          return;
        }

        showNotification('File uploaded successfully!');

        return data.upsertDocument;
      } catch (error) {
        showNotification('Something went wrong', { type: 'error' });
        logError(error);
      }
    },
    [onUpload, showNotification]
  );

  const [onDeleteDocument, { loading: isDeleting }] = useMutation<{ deactivateDocument: boolean }, { id: string }>(
    invoiceApi.deleteInvoiceDocument
  );

  const handleDeleteInvoiceDocument = useCallback(
    async (id: string, fetchOptions?: (api: typeof invoiceApi) => Omit<MutationHookOptions, 'variables'>) => {
      try {
        const { data } = await onDeleteDocument({
          variables: { id },
          ...(typeof fetchOptions === 'function' ? fetchOptions(invoiceApi) : {}),
        });

        if (!data?.deactivateDocument) {
          showNotification('Something went wrong', { type: 'error' });
          return false;
        }

        showNotification('Document deleted successfully!');
        return true;
      } catch (error) {
        showNotification('Something went wrong', { type: 'error' });
        logError(error);
        return false;
      }
    },
    [onDeleteDocument, showNotification]
  );

  return {
    invoiceApi,
    isInvoiceUpdating,
    handleFetchInvoice,
    handleUpdateInvoice,
    isApplyingFeesAndTaxes,
    handleApplyFeesAndTaxes,
    handleUploadInvoice,
    isUploading,
    handleDeleteInvoiceDocument,
    isDeleting,
  };
}
